import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {HTTP_STATUS_OK} from '../../../Constants'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {
    QueryActionTypes,
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {severityNotification} from '../../notification/NotificationConstant'
import {showNotification} from '../../notification/NotificationService'
import {UserPropertyType} from '../UserPropertyConstants'
import {
    addPatientToGeneralPractitioner,
    addPatientToHCP,
    addPatientToPathway,
    addPatientToStaticList,
    addPatientToSupportTeam,
    deleteUserManagement,
    getListUserManagement,
    getListUserPathways,
    getListUserStaticList,
    getPageUserManagements,
    getPageUserSymptoms,
    getSymptomsPathwayByUser,
} from '../UserPropertyEndPoint'

//#region User Static List
export const useListUserStaticList = (
    requestorId,
    simple = false,
    filterRequest = {},
    enabled = true,
) => {
    const {userPropertyKeys} = QueryKeyManager(
        QueryKeyTypes.userProperty,
        requestorId,
    )
    return useQuery({
        queryKey: userPropertyKeys.list({
            type: UserPropertyType.userStaticList,
            simple,
            filterRequest,
        }),
        queryFn: () =>
            getListUserStaticList(requestorId, simple, filterRequest),
        enabled: !!requestorId && !!enabled,
    })
}
//#endregion

//#region User Pathway
export const useListUserPathways = (
    requestorId,
    simple = false,
    filterRequest = {},
    enabled = true,
) => {
    const {userPropertyKeys} = QueryKeyManager(
        QueryKeyTypes.userProperty,
        requestorId,
    )
    return useQuery({
        queryKey: userPropertyKeys.list({
            type: UserPropertyType.userPathway,
            simple,
            filterRequest,
        }),
        queryFn: () => getListUserPathways(requestorId, simple, filterRequest),
        enabled: !!requestorId && !!enabled,
    })
}

export const useSymptomsPathwayByUser = (
    requestorId,
    userId,
    enabled = true,
) => {
    const {userPropertyKeys} = QueryKeyManager(
        QueryKeyTypes.userProperty,
        requestorId,
    )
    return useQuery({
        queryKey: userPropertyKeys.symptomsPathway({userId}),
        queryFn: () => getSymptomsPathwayByUser(requestorId, userId),
        enabled: !!requestorId && !!userId && !!enabled,
    })
}
//#endregion

//#region User Management
export const useListUserManagement = (
    requestorId,
    simple = false,
    filterRequest = {},
    enabled = true,
) => {
    const {userPropertyKeys} = QueryKeyManager(
        QueryKeyTypes.userProperty,
        requestorId,
    )
    return useQuery({
        queryKey: userPropertyKeys.list({
            type: UserPropertyType.userManagement,
            simple,
            filterRequest,
        }),
        queryFn: () =>
            getListUserManagement(requestorId, simple, filterRequest),
        enabled: !!requestorId && !!enabled,
    })
}

export const usePageUserManagements = (
    requestorId,
    pageNumber,
    pageRows,
    sortField,
    sortOrder,
    filterRequest,
    enabled = true,
) => {
    const {userPropertyKeys} = QueryKeyManager(
        QueryKeyTypes.userProperty,
        requestorId,
    )

    return useQuery({
        queryKey: userPropertyKeys.page({
            type: UserPropertyType.userManagement,
            pageNumber,
            pageRows,
            sortField,
            sortOrder,
            filterRequest,
        }),
        queryFn: () =>
            getPageUserManagements(
                requestorId,
                pageNumber,
                pageRows,
                sortField,
                sortOrder,
                filterRequest,
            ),
        enabled: !!requestorId && !!enabled,
    })
}
//#endregion

//#region User Symptom
export const usePageUserSymptoms = (
    requestorId,
    pageNumber,
    pageRows,
    sortField,
    sortOrder,
    filterRequest,
    enabled = true,
) => {
    const {userPropertyKeys} = QueryKeyManager(
        QueryKeyTypes.userProperty,
        requestorId,
    )

    return useQuery({
        queryKey: userPropertyKeys.page({
            type: UserPropertyType.userSymptom,
            pageNumber,
            pageRows,
            sortField,
            sortOrder,
            filterRequest,
        }),
        queryFn: () =>
            getPageUserSymptoms(
                requestorId,
                pageNumber,
                pageRows,
                sortField,
                sortOrder,
                filterRequest,
            ),
        enabled: !!requestorId && !!enabled,
    })
}

export const usePageUserSymptomFetchMore = requestorId => {
    const {userPropertyKeys} = QueryKeyManager(
        QueryKeyTypes.userProperty,
        requestorId,
    )

    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({
            pageNumber,
            pageRows,
            sortField,
            sortOrder,
            filterRequest,
            actionType,
        }) =>
            getPageUserSymptoms(
                requestorId,
                pageNumber,
                pageRows,
                sortField,
                sortOrder,
                filterRequest,
            ),
        onSuccess: (data, variables) => {
            // Data is the response from the server
            // Variables is the data that was passed to the mutationFn
            const {pageRows, sortField, sortOrder, filterRequest, actionType} =
                variables

            if (data?.content?.length > 0) {
                // PageNumber = 0 means it's referencing to the original page
                queryClient.setQueryData(
                    userPropertyKeys.page({
                        type: UserPropertyType.userSymptom,
                        pageNumber: 0,
                        pageRows,
                        sortField,
                        sortOrder,
                        filterRequest,
                    }),
                    oldData => {
                        if (actionType === QueryActionTypes.replace) {
                            return data
                        }

                        return {
                            ...oldData,
                            ...data,
                            content: [...oldData.content, ...data.content],
                        }
                    },
                )
            }
        },
    })
}
//#endregion

//#region Assign Patient To
export const useAssignPatient = (userPropertyType, requestorId) => {
    const {userPropertyKeys} = QueryKeyManager(
        QueryKeyTypes.supportTeam,
        requestorId,
    )
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: data =>
            userPropertyType === UserPropertyType.userPathway
                ? addPatientToPathway(requestorId, data)
                : userPropertyType === UserPropertyType.userStaticList
                ? addPatientToStaticList(requestorId, data)
                : userPropertyType === UserPropertyType.userManagement
                ? addPatientToHCP(requestorId, data)
                : userPropertyType === UserPropertyType.userSupportTeam
                ? addPatientToSupportTeam(requestorId, data)
                : userPropertyType === UserPropertyType.generalPractitioner &&
                  addPatientToGeneralPractitioner(requestorId, data),
        onSuccess: (data, variables) => {
            // Data is the response from the server
            // Variables is the data that was passed to the mutationFn
            if (data === HTTP_STATUS_OK || data?.id) {
                if (userPropertyType === UserPropertyType.userManagement) {
                    showNotification(
                        severityNotification.success,
                        'Patients assigned to HCP successfully !!',
                    )
                } else if (userPropertyType === UserPropertyType.userPathway) {
                    showNotification(
                        severityNotification.success,
                        'Patients assigned to pathway successfully !!',
                    )
                } else if (
                    userPropertyType === UserPropertyType.userStaticList
                ) {
                    showNotification(
                        severityNotification.success,
                        'Patients assigned to static list successfully !',
                    )
                } else if (
                    userPropertyType === UserPropertyType.userSupportTeam
                ) {
                    showNotification(
                        severityNotification.success,
                        'Patients assigned to support team successfully !',
                    )
                } else if (
                    userPropertyType === UserPropertyType.generalPractitioner
                ) {
                    showNotification(
                        severityNotification.success,
                        'Patients assigned to general practitioner successfully !',
                    )
                }
            }
        },
    })
}
//#endregion

export const useUserManagementCRUD = (type, requestorId) => {
    const {userPropertyKeys} = QueryKeyManager(
        QueryKeyTypes.userProperty,
        requestorId,
    )
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: data =>
            type === QueryCRUDTypes.create || type === QueryCRUDTypes.update
                ? data
                : type === QueryCRUDTypes.delete
                ? deleteUserManagement(requestorId, data?.id)
                : data,
        onSuccess: (data, variables) => {
            // Data is the response from the server
            // Variables is the data that was passed to the mutationFn
            if (data === HTTP_STATUS_OK || data?.id) {
                if (
                    type === QueryCRUDTypes.create ||
                    type === QueryCRUDTypes.update
                ) {
                    showNotification(
                        severityNotification.success,
                        'Assign Role Success !!',
                    )
                } else if (type === QueryCRUDTypes.delete) {
                    showNotification(
                        severityNotification.success,
                        'Deletion performed successfully!!',
                    )
                }
            }
        },
    })
}
